// Components==============
import { motion } from 'framer-motion';
import { useMediaQ } from 'hooks/useMediaQ';
import React, { useContext, useEffect, useRef, useState } from 'react';
import './Tooltip.module.scss';
import { TooltipContext } from './TooltipContextWrapper';
// =========================

type Position = 'top' | 'right' | 'bottom' | 'left' | 'bottom-right';

type Props = {
  content: string | React.ReactNode;
  children: React.ReactNode | string;
  position?: Position;
  fullWidth?: boolean;
  noPointer?: boolean;
  disabled?: boolean;
  clickContent?: string | React.ReactNode;
  forceTop?: boolean;
};

export default function Tooltip({
  content,
  children,
  position = 'top',
  fullWidth = false,
  noPointer = false,
  disabled,
  clickContent,
  forceTop = false,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  const {
    setTooltipIsVisible,
    setTooltipPosition,
    setTooltipProps,
    tooltipProps,
    tooltipPosition,
    tooltipIsVisible,
  } = useContext(TooltipContext);

  const elementDimensions = ref.current?.getClientRects()?.item(0);

  const left = elementDimensions
    ? elementDimensions?.x + elementDimensions?.width / 2
    : 0;
  const top = elementDimensions
    ? elementDimensions?.y + elementDimensions?.height / 2
    : 0;

  const [trigger, setTrigger] = useState<{
    type: 'hover' | 'clicked';
    state: boolean;
  }>({ type: 'hover', state: false });

  const query = useMediaQ('min', 1024);

  useEffect(() => {
    if (ref.current && elementDimensions) {
      const shouldGetClickedProps = query
        ? trigger.type === 'clicked' && !tooltipProps.clicked
        : trigger.type === 'clicked';

      setTooltipProps({
        content: shouldGetClickedProps ? clickContent : content,
        position: forceTop
          ? 'top'
          : shouldGetClickedProps
          ? 'bottom'
          : position,
        noPointer,
        clicked: !!shouldGetClickedProps,
      });

      setTooltipPosition({
        left,
        top,
      });
    }
  }, [trigger.state]);

  if (disabled) return <>{children}</>;

  return (
    <motion.div
      styleName={`wrapper ${fullWidth ? 'full-width' : ''}`}
      onHoverStart={() => {
        if (tooltipProps.clicked) return;
        setTooltipIsVisible(true);
        setTrigger((prev) => ({ type: 'hover', state: !prev.state }));
      }}
      onHoverEnd={() => {
        if (tooltipProps.clicked) return;
        setTooltipIsVisible(false);
        setTrigger((prev) => ({ type: 'hover', state: !prev.state }));
      }}
      onClick={
        clickContent
          ? () => {
              setTrigger((prev) => ({ type: 'clicked', state: !prev.state }));
              if (tooltipIsVisible && left !== tooltipPosition.left) return;
              if (!query) setTooltipIsVisible((prev) => !prev);
            }
          : undefined
      }
      style={{ cursor: clickContent ? 'pointer' : 'default' }}
      ref={ref}
    >
      {children}
    </motion.div>
  );
}
